// components/UserAnalytics.jsx
import React, { useState } from 'react';
import {
    Card,
    CardContent,
    Typography,
    Grid,
    Avatar,
    Skeleton,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TablePagination,
    Box,
    useTheme
} from '@mui/material';
import { Bar, Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';
import { transformToBTC } from '../../utils/helpers';
import { useUserAnalytics } from '../../hooks';
import { useNavigate } from 'react-router-dom';
import { useBitcoinPrice } from '../../state/context/bitcoin.context';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
);

const UserAnalytics = ({ userId }) => {
    const navigate = useNavigate();
    const theme = useTheme();
    const { data, loading, error } = useUserAnalytics(userId);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const { t } = useTranslation();
    const { price: btcPrice } = useBitcoinPrice();

    if (loading) {
        return (
            <Skeleton
                variant="rectangular"
                sx={{ borderRadius: 2, width: '100%', height: 300 }}
            />
        );
    }
    if (error) {
        return (
            <Typography color="error" sx={{ m: 2 }}>
                Error: {error.message}
            </Typography>
        );
    }

    // Prepare charts
    const topTokensData = {
        labels: data.topTokens.map((t) => t.ticker),
        datasets: [
            {
                label: t('user.totalVolume'),
                data: data.topTokens.map((t) => t.totalVolume / 1e11),
                backgroundColor: theme.palette.primary.main,
            },
        ],
    };

    const dailyVolumeData = {
        labels: data.dailyVolume.map((r) => r._id),
        datasets: [
            {
                label: t('user.dailyBTCVolume'),
                data: data.dailyVolume.map((r) => r.dailyBTCVolume / 1e11),
                fill: false,
                borderColor: theme.palette.secondary.main,
            },
        ],
    };

    const chartOptions = {
        maintainAspectRatio: false,
        plugins: {
            legend: {
                labels: {
                    color: theme.palette.text.secondary
                }
            },
            title: {
                color: theme.palette.text.primary
            },
        },
        scales: {
            x: {
                ticks: {
                    color: theme.palette.text.secondary
                },
                grid: {
                    color: theme.palette.mode === 'light'
                        ? 'rgba(0,0,0,0.1)'
                        : 'rgba(255,255,255,0.1)'
                },
            },
            y: {
                ticks: {
                    color: theme.palette.text.secondary
                },
                grid: {
                    color: theme.palette.mode === 'light'
                        ? 'rgba(0,0,0,0.1)'
                        : 'rgba(255,255,255,0.1)'
                },
            },
        },
    };

    const paginatedDaily = data.dailyVolume.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    const handleChangePage = (_, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (e) => {
        setRowsPerPage(+e.target.value);
        setPage(0);
    };

    return (
        <Card
            sx={{
                borderRadius: 2,
                boxShadow: 3,
                m: 2,
            }}
        >
            <CardContent>
                <Typography
                    variant="h5"
                    sx={{ fontWeight: 'bold', mb: 3 }}
                >
                    {t('user.analytics')}
                </Typography>

                <Grid container spacing={2}>
                    {/* Top Traded Tokens */}
                    <Grid item xs={12} md={6}>
                        <Typography variant="h6" sx={{ mb: 1 }}>
                            {t('user.topTradedTokens')}
                        </Typography>

                        <Box sx={{ height: 400, mb: 2 }}>
                            <Bar data={topTokensData} options={chartOptions} />
                        </Box>

                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>{t('common.tokenId')}</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>{t('user.totalVolume')}</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>{t('user.tradeCount')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.topTokens.map((token) => (
                                    <TableRow
                                        key={token._id}
                                        onClick={() => window.open(`/tokens/${ token._id }`)}
                                        sx={{
                                            cursor: 'pointer',
                                            '&:hover': {
                                                bgcolor: theme.palette.mode === 'light'
                                                    ? 'rgba(0,0,0,0.04)'
                                                    : 'rgba(255,255,255,0.1)'
                                            },
                                        }}
                                    >
                                        <TableCell
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                            }}
                                        >
                                            <Avatar
                                                alt={token.name}
                                                src={`https://images.odin.fun/token/${ token._id }`}
                                                sx={{ width: 24, height: 24 }}
                                            />
                                            {token.ticker}
                                        </TableCell>
                                        <TableCell>
                                            <Box>
                                                <Typography variant="body2">
                                                    {transformToBTC(token.totalVolume)}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    ${((token.totalVolume / 1e11) * btcPrice).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            {token.tradeCount}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Grid>

                    {/* Daily Volume Trends */}
                    <Grid item xs={12} md={6}>
                        <Typography variant="h6" sx={{ mb: 1 }}>
                            {t('user.dailyVolumeTrends')}
                        </Typography>

                        <Box sx={{ height: 400, mb: 2 }}>
                            <Line data={dailyVolumeData} options={chartOptions} />
                        </Box>

                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>{t('common.date')}</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>{t('user.btcVolume')}</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>{t('user.tradeCount')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedDaily.map((rec) => (
                                    <TableRow key={rec._id}>
                                        <TableCell>{rec._id}</TableCell>
                                        <TableCell>
                                            <Box>
                                                <Typography variant="body2">
                                                    {transformToBTC(rec.dailyBTCVolume)}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    ${((rec.dailyBTCVolume / 1e11) * btcPrice).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            {rec.tradeCount}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>

                        <TablePagination
                            component="div"
                            count={data.dailyVolume.length}
                            page={page}
                            onPageChange={handleChangePage}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
};

export default UserAnalytics;
